import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Mail, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface EmailMessage {
  gmailMessageId: string;
  subject: string;
  from: string;
  date?: string;
  isRead?: boolean;
}

interface EmailAccount {
  id: string;
  email: string;
  isActive: boolean;
}

interface GmailPreviewWidgetProps {
  onConnectClick: () => void;
}

export default function GmailPreviewWidget({ onConnectClick }: GmailPreviewWidgetProps) {
  const [, navigate] = useLocation();

  const { data: accounts = [] } = useQuery<EmailAccount[]>({
    queryKey: ['/api/gmail/accounts'],
  });

  const hasAccount = accounts.length > 0;

  const { data: emails = [], isLoading } = useQuery<EmailMessage[]>({
    queryKey: ['/api/gmail/emails/recent'],
    enabled: hasAccount,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'short' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Recent Emails
          </CardTitle>
          <CardDescription>Latest inbox messages</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!hasAccount) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Recent Emails
          </CardTitle>
          <CardDescription>Latest inbox messages</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-center py-4">
          <p className="text-sm text-muted-foreground">No email account connected</p>
          <Button size="sm" onClick={onConnectClick} data-testid="button-gmail-connect-widget">
            Connect Email
          </Button>
        </CardContent>
      </Card>
    );
  }

  const previewEmails = emails.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Recent Emails
        </CardTitle>
        <CardDescription>Latest inbox messages</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {previewEmails.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">Inbox is empty</p>
        ) : (
          <div className="divide-y">
            {previewEmails.map((email) => (
              <div
                key={email.gmailMessageId}
                className={cn(
                  "px-4 py-3 cursor-pointer hover:bg-accent transition-colors",
                  !email.isRead && "bg-blue-50 dark:bg-blue-950/20"
                )}
                onClick={() => navigate('/email')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') navigate('/email');
                }}
                data-testid={`email-preview-row-${email.gmailMessageId}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className={cn("text-sm truncate", !email.isRead ? "font-semibold" : "font-medium")}>
                      {email.from?.split('<')[0].trim() || email.from}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {email.subject || '(No Subject)'}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDate(email.date)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="px-4 py-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => navigate('/email')}
            data-testid="button-gmail-view-all"
          >
            View All
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
